<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  components: {
    ComponentProxy,
  },
});
</script>
<script setup lang="ts" generic="TData extends object">
import 'ag-grid-enterprise';
import { AgGridVue } from 'ag-grid-vue';
import { AgTableStyles } from './';
import { computed, inject, nextTick, shallowRef, watch, watchEffect } from 'vue';
import {
  CellClickedEvent,
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  ExportParams,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  ManagedGridOptionKey,
  RowClassParams,
  RowClassRules,
  SortModelItem,
} from 'ag-grid-enterprise';
import { ComponentProxy } from './columns/utils';
import { persistColumnState, retrieveColumnState } from './column-state';
import {
  columnKeysExcl,
  processCsvCellCallback,
  processExcelCellCallback,
} from '@/modules/common/components/ag-table/export';
import {
  dataTypeDefinitions,
  setDefaultUseValueFormatterForExport,
} from '@/modules/common/components/ag-table/columns/common';
import { useAuthStore } from '@/store/auth-store';

// @TODO: we should type provide/inject after we migrate all components to composition API
// (https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject)
const gridStateKey = inject<string | undefined>('gridStateKey', undefined);

const authStore = useAuthStore();

;

const props = withDefaults(
  defineProps<{
    tableId?: string;
    sort?: SortModelItem;
    noSort?: boolean;
    noMenu?: boolean;
    noMove?: boolean;
    rowDrag?: boolean;
    getRowId: (data: TData) => string;
    getRowClass?: (params: RowClassParams<TData>) => string;
    rowData: TData[];
    selectedItems?: TData[];
    pageSize?: number;
    pageSizeSelector?: number[];
    columnDefs: Array<ColDef<TData>>;
    isRowSelectable?: (node: IRowNode<TData>) => boolean;
    summary?: TData[];
    rowClassRules?: RowClassRules<TData>;
    fitFullWidth?: boolean;
    autoGrow?: boolean;
    defaultExportParams?: ExportParams<never>;
    rowCount?: number;
    searchText?: string;
  }>(),
  {
    tableId: undefined,
    selectedItems: () => [],
    pageSize: 100,
    sort: undefined,
    noSort: false,
    noMenu: false,
    noMove: false,
    rowDrag: false,
    pageSizeSelector: () => [100, 500, 1000],
    isRowSelectable: () => true,
    summary: () => [],
    rowClassRules: undefined,
    fitFullWidth: false,
    autoGrow: false,
    defaultExportParams: undefined,
    getRowClass: undefined,
    searchText: '',
    rowCount: 0,
  }
);

const emit = defineEmits<{
  (
    event: 'ready',
    config: {
      resetSelection: () => void;
      selectAll: () => void;
      setQuickFilterText: (text: string) => void;
      setGridOption: (option: ManagedGridOptionKey, value: unknown) => void;
      redrawRows: () => void;
    }
  ): void;
  (event: 'update:selected-items' | 'update:row-order', items: TData[]): void;
  (event: 'update:page' | 'update:page-size', payload: number): void;
  (event: 'click:cell', e: CellClickedEvent<TData>): void;
}>();

let gridApi: GridApi | null = null;

const initialState = computed(() =>
  retrieveColumnState(authStore.user?.id, gridStateKey, props.tableId, props.columnDefs)
);

const autoSizeStrategy = computed(() => ({
  type: props.fitFullWidth ? 'fitGridWidth' : undefined,
}));

function onFirstDataRendered(params: FirstDataRenderedEvent<TData>) {
  // Set initial selection based on props.selectedItems
  if (props.selectedItems && props.selectedItems.length > 0) {
    const selectedIds = props.selectedItems.map(props.getRowId);
    params.api.forEachNode((node) => {
      if (node.data && selectedIds.includes(props.getRowId(node.data))) {
        node.setSelected(true);
      }
    });
  }

  if (initialState.value !== null || props.fitFullWidth) return;

  params.api.autoSizeAllColumns();

  const newColumnWidths = (params.api.getColumns() ?? [])
    .map((column) => {
      const colDef = column.getColDef();
      return colDef.initialWidth === undefined || column.getActualWidth() <= colDef.initialWidth
        ? undefined
        : { key: colDef, newWidth: colDef.initialWidth };
    })
    .filter(Boolean) as Array<{
    key: ColDef;
    newWidth: number;
  }>;
  params.api.setColumnWidths(newColumnWidths);
}

const localSelectedItems = shallowRef(props.selectedItems);
watchEffect(() => (localSelectedItems.value = props.selectedItems));
watch(localSelectedItems, (selectedItems) => emit('update:selected-items', selectedItems));

const localRowData = shallowRef(props.rowData);
watch(
  () => props.rowData,
  async () => {
    if (!gridApi) {
      localRowData.value = props.rowData;
      return;
    }

    // we need to save the values for later use
    const currentSelectedIds = localSelectedItems.value.map((item) => props.getRowId(item));

    updateRows(props.rowData);
    await nextTick();

    // restore selection after items are updated (so we don't have stale objects selected)
    gridApi.forEachNode((node) => {
      if (currentSelectedIds.includes(props.getRowId(node.data))) {
        node.setSelected(true);
      }
    });
    localSelectedItems.value = gridApi.getSelectedRows() ?? [];
  }
);

const paginationPageSizeSelector = computed(() =>
  props.pageSize === undefined
    ? undefined
    : [...props.pageSizeSelector, props.pageSize].filter((e, i, a) => a.indexOf(e) === i).sort()
);

const showPagination = computed(() => {
  if (props.rowCount) {
    return props.rowCount > props.pageSize;
  } else {
    return localRowData.value.length > props.pageSize;
  }
});

const columnDefs = computed(() => {
  const colDefs = props.columnDefs.map((colDef) => {
    // apply our own props
    colDef.sortable = (colDef.sortable ?? true) && !props.noSort;
    colDef.suppressHeaderMenuButton ||= props.noMenu;
    colDef.suppressMovable ||= props.noMove;

    if (colDef.headerCheckboxSelection) {
      colDef.headerCheckboxSelectionCurrentPageOnly = true;
    }

    if (props.sort !== undefined) {
      const { colId, sort } = props.sort;
      if (colDef.colId === colId) {
        colDef.initialSort = sort;
      }
    }

    setDefaultUseValueFormatterForExport(colDef);

    return colDef;
  });

  const orderedColIds = new Map(
    initialState.value?.columnOrder?.orderedColIds.map((id, index) => [id, index])
  );
  if (orderedColIds.size) {
    // we have to manually sort the cols in the order they were saved
    // because when we apply initialState to AgGrid it doesn't respect ordering
    colDefs.sort((a, b) => {
      const aIndex = orderedColIds.get(a.colId ?? '');
      const bIndex = orderedColIds.get(b.colId ?? '');
      // one of them wasn't there, no swap needed
      if (aIndex === undefined || bIndex === undefined) return 0;
      return aIndex - bIndex;
    });
  }
  return colDefs;
});

function onGridReady(params: GridReadyEvent): void {
  gridApi = params.api;

  if (gridApi) {
    if (props.fitFullWidth) {
      gridApi.sizeColumnsToFit();
    }
  }

  emit('ready', {
    resetSelection: () => gridApi?.deselectAll(),
    selectAll: () => gridApi?.selectAll(),
    setQuickFilterText: (text: string) => gridApi?.setGridOption('quickFilterText', text),
    setGridOption: (option: ManagedGridOptionKey, value: unknown) =>
      gridApi?.setGridOption(option, value),
    redrawRows: () => gridApi?.redrawRows(),
  });
}

function updateRows(items: TData[]) {
  const updatedItemsById = items.reduce<Record<string, TData>>((acc, item) => {
    acc[props.getRowId(item)] = item;
    return acc;
  }, {});

  const itemsToUpdate: TData[] = [];
  const itemsToRemove: TData[] = [];

  gridApi!.forEachNode((rowNode) => {
    const updatedItem = updatedItemsById[rowNode.id as string];

    if (updatedItem) {
      itemsToUpdate.push(updatedItem);
      delete updatedItemsById[rowNode.id as string];
    } else {
      itemsToRemove.push(rowNode.data);
    }
  });

  gridApi!.applyTransaction({
    // items that weren't updated or removed are new
    add: Object.values(updatedItemsById),
    update: itemsToUpdate,
    remove: itemsToRemove,
  });
}

function updateSelectedItems(): void {
  if (gridApi === null) return;

  // we want to modify the existing selection to keep it on page change
  const selectedItems = new Set<TData>(localSelectedItems.value);
  gridApi.forEachNode((node) => {
    // Skip nodes without data (e.g., group headers, footer rows)
    if (!node.data) return;

    if (node.isSelected()) {
      selectedItems.add(node.data);
    } else {
      // we only remove existing, but not selected nodes
      selectedItems.delete(node.data);
    }
  });
  localSelectedItems.value = [...selectedItems];
}

function onSelectionChanged(): void {
  updateSelectedItems();
}

function onFilterChanged() {
  updateSelectedItems();
}

function onRowDragEnd(): void {
  if (!gridApi) return;

  // Get the current row order from the grid
  const reorderedItems: TData[] = [];

  // Use forEachNode with rowModel order to get rows in their display order
  gridApi.forEachNode((node) => {
    if (node.data) {
      reorderedItems.push(node.data);
    }
  });

  emit('update:row-order', reorderedItems);
}

function saveColumnState(): void {
  persistColumnState(authStore.user?.id, gridStateKey, gridApi, props.tableId);
}

function getVisibleColumnDefs() {
  return (gridApi?.getAllDisplayedColumns() ?? []).map((def) => def.getColDef());
}

const excelExportParams = computed(() => {
  return {
    author: 'Provable Markets',
    sheetName: 'export',
    // we exclude some common columns from the export
    columnKeys: columnKeysExcl(getVisibleColumnDefs(), 'actions', 'checkbox'),
    processCellCallback: processExcelCellCallback,
    ...props.defaultExportParams,
  } as ExcelExportParams;
});

const csvExportParams = computed(() => {
  return {
    // we exclude some common columns from the export
    columnKeys: columnKeysExcl(getVisibleColumnDefs(), 'actions', 'checkbox'),
    processCellCallback: processCsvCellCallback,
    ...props.defaultExportParams,
  } as CsvExportParams;
});
</script>

<template>
  <AgTableStyles v-slot="slotProps" :data-test-id="tableId">
    <AgGridVue
      v-bind="slotProps"
      :auto-size-padding="15"
      :auto-size-strategy="initialState === null ? autoSizeStrategy : undefined"
      :column-defs="columnDefs"
      :data-type-definitions="dataTypeDefinitions"
      :default-csv-export-params="csvExportParams"
      :default-excel-export-params="excelExportParams"
      :dom-layout="autoGrow ? 'autoHeight' : 'normal'"
      :get-row-class="getRowClass"
      :get-row-id="(params) => getRowId(params.data)"
      :initial-state="initialState"
      :is-row-selectable="isRowSelectable"
      maintain-column-order
      :pagination="showPagination"
      :pagination-page-size="pageSize"
      :pagination-page-size-selector="paginationPageSizeSelector"
      :pinned-bottom-row-data="summary"
      :row-class-rules="rowClassRules"
      :row-data="localRowData"
      :row-drag-managed="rowDrag"
      row-model-type="clientSide"
      suppress-scroll-on-new-data
      v-on="$listeners"
      @cell-clicked="(event) => emit('click:cell', event)"
      @column-moved="saveColumnState"
      @column-pinned="saveColumnState"
      @column-resized="saveColumnState"
      @column-visible="saveColumnState"
      @filter-changed="onFilterChanged"
      @first-data-rendered="onFirstDataRendered"
      @grid-ready="onGridReady"
      @row-drag-end="onRowDragEnd"
      @selection-changed="onSelectionChanged"
      @sort-changed="saveColumnState"
    />
  </AgTableStyles>
</template>

<style lang="scss" scoped></style>
